import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { SupplyItem } from '@/services/api';
import { formatFieldValue } from '@/components/feature/GreenCertFieldConfig';

interface SupplySummaryProps {
  items: SupplyItem[];
}

function getImageSrc(projectTypeVals: string[]): string {
  const joined = projectTypeVals.join(',');
  if (joined.includes('wind')) return '/images/supply-wind.jpg';
  if (joined.includes('pv')) return '/images/supply-solar.jpg';
  if (joined.includes('hydro')) return '/images/supply-hydro.jpg';
  return '/images/supply-default.jpg';
}

export default function SupplySummary({ items }: SupplySummaryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeItems = items.filter((s) => s.status === 'active').slice(0, 6);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="w-full bg-background-50 py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-3">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              最新供给
            </h2>
            <p className="text-foreground-500 text-sm mt-2">
              实时更新绿证挂牌信息
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/supply"
              className="px-4 py-2 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 &rarr;
            </Link>
            <button
              onClick={() => scroll('left')}
              className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="向左滚动"
            >
              <i className="ri-arrow-left-line text-foreground-600"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="向右滚动"
            >
              <i className="ri-arrow-right-line text-foreground-600"></i>
            </button>
          </div>
        </div>

        {/* Scrollable cards */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {activeItems.map((item, idx) => {
            const projectLabel = formatFieldValue('projectType', item.projectType);
            return (
              <div
                key={item.id}
                className="flex-shrink-0 w-[300px] md:w-[320px] rounded-lg overflow-hidden relative group cursor-pointer"
              >
                {/* Background image */}
                <div className="relative h-[200px] w-full">
                  <img
                    src={getImageSrc(item.projectType)}
                    alt={item.company}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                  {/* Price/year tag */}
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-primary-500/90 text-background-50 text-xs font-medium rounded-md">
                      &yen;{item.price}/个
                    </span>
                  </div>

                  {/* Bottom info */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-background-50 font-bold text-base leading-snug">
                      {item.company}
                    </h3>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-background-100/80 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {item.region}
                      </span>
                      <span>{projectLabel}</span>
                      <span>{item.quantity.toLocaleString()}个</span>
                      <span>{item.year}年度</span>
                    </div>
                    <Link
                      to={`/supply/${item.id}`}
                      className="inline-block mt-3 text-primary-300 text-xs font-medium hover:text-primary-200 transition-colors cursor-pointer"
                    >
                      查看详情 &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}